import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authAPI } from '../lib/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('propchain_token')
    if (token) {
      try {
        const response = await authAPI.getMe()
        setUser(response.data)
      } catch (error) {
        console.error('Failed to load user:', error)
        localStorage.removeItem('propchain_token')
        localStorage.removeItem('propchain_user')
      }
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password })
    const { access_token, user: userData } = response.data
    localStorage.setItem('propchain_token', access_token)
    localStorage.setItem('propchain_user', JSON.stringify(userData))
    setUser(userData)
    return userData
  }

  const register = async (email, password, name, role = 'investor') => {
    const response = await authAPI.register({ email, password, name, role })
    const { access_token, user: userData } = response.data
    localStorage.setItem('propchain_token', access_token)
    localStorage.setItem('propchain_user', JSON.stringify(userData))
    setUser(userData)
    return userData
  }

  const loginWithWallet = async (address, signature, message) => {
    const response = await authAPI.verifyWallet(address, signature, message)
    const { access_token, user: userData } = response.data
    localStorage.setItem('propchain_token', access_token)
    localStorage.setItem('propchain_user', JSON.stringify(userData))
    setUser(userData)
    return userData
  }

  const logout = () => {
    localStorage.removeItem('propchain_token')
    localStorage.removeItem('propchain_user')
    setUser(null)
  }

  const updateUser = (updatedUser) => {
    setUser(updatedUser)
    localStorage.setItem('propchain_user', JSON.stringify(updatedUser))
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      loginWithWallet,
      logout,
      loading,
      updateUser,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isIssuer: user?.role === 'issuer',
      isInvestor: user?.role === 'investor',
      isKYCApproved: user?.kyc_status === 'approved',
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
